/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getIsActivityV3RecentsEnabled } from '@confluence/activity-v3-enabled/entry-points/getIsActivityV3RecentsEnabled';

import type {
	RecentListHomeRecentWorkQuery as RecentListHomeRecentWorkQuery$data,
	RecentListHomeRecentWorkQueryVariables as RecentListHomeRecentWorkQuery$variables,
} from './__types__/RecentListHomeRecentWorkQuery';
import type {
	RecentListHomeRecentWorkV3Query as RecentListHomeRecentWorkV3Query$data,
	RecentListHomeRecentWorkV3QueryVariables as RecentListHomeRecentWorkV3Query$variables,
} from './__types__/RecentListHomeRecentWorkV3Query';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers';

export type QueryResult = RecentListHomeRecentWorkQuery$data | RecentListHomeRecentWorkV3Query$data;

export type QueryVariables =
	| RecentListHomeRecentWorkQuery$variables
	| RecentListHomeRecentWorkV3Query$variables;

export const useHomeRecentWorkData = ({
	isWhiteboardViewedEnabled,
	isDatabaseEnabled,
	first,
}: {
	isWhiteboardViewedEnabled: boolean;
	isDatabaseEnabled: boolean;
	first?: number;
}) => {
	const { cloudId, isLoggedIn } = useSessionData();
	const isActivityV3RecentsEnabled = getIsActivityV3RecentsEnabled();

	const { data, loading, error } = useQuery<QueryResult, QueryVariables>(
		isActivityV3RecentsEnabled
			? gql`
					query useHomeRecentWorkDataV3Query(
						$cloudId: ID!
						$first: Int
						$after: String
						$objectTypes: [String!]
					) {
						activity {
							myActivity {
								all(
									filter: {
										type: AND
										rootContainerIds: [$cloudId]
										arguments: { products: ["CONFLUENCE"], objectTypes: $objectTypes }
									}
									first: $first
									after: $after
								) {
									edges {
										cursor
										node {
											id
											object {
												id
												data {
													... on ConfluencePage {
														id
														title
														type
														subtype
														pageStatus: status
														createdAt
														links {
															webUi
															editUi
														}
														metadata {
															titleEmojiDraft {
																value
															}
															titleEmojiPublished {
																value
															}
														}
														space {
															alias
															id
															key
															name
														}
													}
													... on ConfluenceBlogPost {
														id
														title
														type
														blogStatus: status
														createdAt
														links {
															webUi
															editUi
														}
														metadata {
															titleEmojiDraft {
																value
															}
															titleEmojiPublished {
																value
															}
														}
														space {
															alias
															id
															key
															name
														}
													}
													... on ConfluenceWhiteboard {
														id
														title
														type
														status
														links {
															webUi
														}
														space {
															alias
															id
															key
															name
														}
													}
													... on ConfluenceDatabase {
														id
														title
														type
														status
														links {
															webUi
														}
														space {
															alias
															id
															key
															name
														}
													}
													... on ConfluenceEmbed {
														id
														title
														type
														status
														links {
															webUi
														}
														metadata {
															titleEmojiDraft {
																value
															}
															titleEmojiPublished {
																value
															}
														}
														space {
															alias
															id
															key
															name
														}
													}
												}
											}
											event {
												timestamp
												eventType
											}
										}
									}
								}
							}
						}
					}
				`
			: gql`
					query useHomeRecentWorkDataQuery(
						$cloudId: ID!
						$first: Int
						$after: String
						$objectTypes: [ActivitiesObjectType!]
					) {
						activities {
							myActivities {
								all(
									filters: [
										{
											type: AND
											arguments: {
												products: [CONFLUENCE]
												objectTypes: $objectTypes
												cloudIds: [$cloudId]
											}
										}
									]
									first: $first
									after: $after
								) {
									edges {
										cursor
										node {
											id
											timestamp
											object {
												id
												events {
													eventType
												}
												content {
													id
													title
													subType
													type
													links {
														webui
														editui
													}
													metadata {
														sourceTemplateEntityId
													}
													space {
														alias
														id
														key
														name
													}
													status
													blank
													emoji: properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
														nodes {
															id
															key
															value
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				`,
		getRecentWorkQueryOptions({
			cloudId,
			isLoggedIn,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			first,
			isActivityV3MigrationEnabled: isActivityV3RecentsEnabled,
		}),
	);

	return { data, loading, error };
};
